import { HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

interface SectionProps extends HTMLAttributes<HTMLElement> {
  variant?: 'default' | 'secondary' | 'primary' | 'cta';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const Section = forwardRef<HTMLElement, SectionProps>(
  ({ className, variant = 'default', padding = 'lg', children, ...props }, ref) => {
    const baseStyles = 'w-full';

    const variants = {
      default: '',
      secondary: 'bg-secondary-50',
      primary: 'bg-primary-400 text-white',
      cta: 'bg-cta-400 text-white',
    };

    const paddings = {
      none: '',
      sm: 'py-6',
      md: 'py-8',
      lg: 'py-12 lg:py-16',
    };

    return (
      <section
        ref={ref}
        className={cn(baseStyles, variants[variant], paddings[padding], className)}
        {...props}
      >
        {children}
      </section>
    );
  }
);

Section.displayName = 'Section';

interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'default' | 'narrow' | 'wide' | 'full';
}

const Container = forwardRef<HTMLDivElement, ContainerProps>(
  ({ className, size = 'default', children, ...props }, ref) => {
    const sizes = {
      default: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
      narrow: 'max-w-3xl mx-auto px-4 sm:px-6 lg:px-8',
      wide: 'max-w-8xl mx-auto px-4 sm:px-6 lg:px-8',
      full: 'w-full px-4 sm:px-6 lg:px-8',
    };

    return (
      <div ref={ref} className={cn(sizes[size], className)} {...props}>
        {children}
      </div>
    );
  }
);

Container.displayName = 'Container';

export { Section, Container };
export type { SectionProps, ContainerProps };
